import { compact, sumBy, partition, findLast } from 'lodash';
import { hToMs } from '../../lib/time';
import { get, mappedMax, assertedDefined } from '../../lib/util';
import type { ViolationIndicatorBasis } from '../../state/planning/options';
import type { OrderViolationLimits } from '../ScenarioImprovementParams';
import type { ClientId } from './ClientId';
import type {
  ScheduleData,
  ScheduleOptimizationOpts,
  ComputeScheduleMetricsParams,
} from './Core';
import {
  countTotalIdleTime,
  getMaxLDViolations,
  countTotalConstrainedDelayByOrder,
  DELAY_CONSTRAINT_HOURS,
} from './Core';
import { countTotalMassDistance } from './Core.voyage-metrics';
import { DeriveCapacitiesAndTimes } from './DeriveCapacitiesAndTimes';
import type {
  BestSchedule,
  OrdersToVesselCombination,
} from './suggestions/Suggestions';
import type { TimeData } from './TimeData';
import {
  isCargoEventTimeData,
  isLoadEventTimeData,
  getEventEndLocationId,
  getEventStartLocationId,
} from './TimeData';
import { Objective } from './suggestions/VoyageSuggestionParams';

export type ScheduleMetrics = {
  mass: number;
  shipmentAmount: number;
  voyageDurationH: number;
  totalDelayH: number;
  objectiveScore: number;
  resultValue: BestSchedule;
  maxViolationMs: OrderViolationLimits;
  /** Empty object when no violation exceeds an acceptable level. */
  violations: {
    newAssignmentHasExclusionViolation?: boolean;
    maxEarlyMs?: number;
    maxLateMs?: number;
    totalDelayH?: number;
    maxBallastDistanceNm?: number;
  };
};

export function hasMetricsViolations(metrics: ScheduleMetrics): boolean {
  return Object.keys(metrics.violations).length > 0;
}

/** Returns null if the schedule has too high violations relative to opts given */
export function computeScheduleMetrics(
  combination: OrdersToVesselCombination,
  /** Given when comparing to an earlier state. Affects how delays are considered. */
  originalResults: null | ScheduleData,
  deriveParams: ComputeScheduleMetricsParams,
  opts: ScheduleOptimizationOpts,
  allAvailableOrderIds: Set<number>,
  violationBasis: ViolationIndicatorBasis,
): ScheduleMetrics {
  const { schedule, assignments } = combination;
  const { timeData, totals, inputs } = DeriveCapacitiesAndTimes(
    deriveParams.vessel,
    schedule,
    deriveParams.context,
    deriveParams.config,
    { skipSwapComputation: true, calculateCosts: true },
    deriveParams.now,
  );
  const violations: ScheduleMetrics['violations'] = {};
  const originalHoursAtSea = originalResults?.totals.hoursAtSea ?? 0;
  const combinationHoursAtSea = totals.hoursAtSea;
  const { cost, tcCost, distance, ballastDistance, mass } = totals;
  const totalIdle = countTotalIdleTime(timeData, opts.countIdleFromIndex ?? 0);

  const assignedShipmentIds = new Set<ClientId>(
    compact(assignments.map((a) => a.order.shipment?.clientId)),
  );

  const tdBelongsToAssignedShipment = (td: TimeData) =>
    isCargoEventTimeData(td) &&
    ((td.event.order.shipment &&
      assignedShipmentIds.has(td.event.order.shipment.clientId)) ||
      allAvailableOrderIds.has(td.event.order.id));

  const shipmentTds = timeData.filter(tdBelongsToAssignedShipment);

  if (opts.respectCargoExclusions) {
    const newAssignmentHasExclusionViolation = shipmentTds.some(
      (td) => td.assortmentExclusions.length > 0,
    );
    if (newAssignmentHasExclusionViolation) {
      violations.newAssignmentHasExclusionViolation = true;
    }
  }

  // Check if any of the new events start too early or late.
  const maxViolationMs = getMaxLDViolations(shipmentTds, violationBasis);
  if (maxViolationMs.load.early > (opts.violationLimitsMs?.load.early ?? 0)) {
    violations.maxEarlyMs = maxViolationMs.load.early;
  }
  if (
    maxViolationMs.discharge.early >
    (opts.violationLimitsMs?.discharge.early ?? 0)
  ) {
    violations.maxEarlyMs = Math.max(
      violations.maxEarlyMs ?? 0,
      maxViolationMs.discharge.early,
    );
  }
  if (maxViolationMs.load.late > (opts.violationLimitsMs?.load.late ?? 0)) {
    violations.maxLateMs = maxViolationMs.load.late;
  }
  if (
    maxViolationMs.discharge.late >
    (opts.violationLimitsMs?.discharge.late ?? 0)
  ) {
    violations.maxLateMs = Math.max(
      violations.maxLateMs ?? 0,
      maxViolationMs.discharge.late,
    );
  }

  const loadTimeDatas = shipmentTds.filter((td) => isLoadEventTimeData(td));

  const shipmentAmount = sumBy(loadTimeDatas, (ltd) => ltd.amount);

  const [shipmentOrderTds, nonShipmentOrderTds] = partition(timeData, (td) =>
    tdBelongsToAssignedShipment(td),
  );
  const firstShipmentTdIndex = timeData.findIndex(tdBelongsToAssignedShipment);
  const firstShipmentTd = get(timeData, firstShipmentTdIndex);
  const tdPrecedingShipment = get(timeData, firstShipmentTdIndex - 1);
  const locationPrecedingShipment =
    tdPrecedingShipment?.event &&
    getEventEndLocationId(tdPrecedingShipment.event);
  const shipmentBeginsInExistingPortCall =
    !!firstShipmentTd &&
    getEventStartLocationId(firstShipmentTd.event) ===
      locationPrecedingShipment;

  /** Delays of all orders in the whole schedule */
  const delayByNonShipmentOrderId =
    countTotalConstrainedDelayByOrder(nonShipmentOrderTds);

  const originalDelayByOrderId =
    originalResults?.timeData &&
    countTotalConstrainedDelayByOrder(originalResults.timeData);

  /** How much worse existing delays became */
  const nonShipmentDelayMaxDeltaH =
    originalDelayByOrderId &&
    (delayByNonShipmentOrderId.size === 0
      ? 0
      : mappedMax(
          delayByNonShipmentOrderId.entries(),
          ([orderId, delay]) =>
            delay - (originalDelayByOrderId.get(orderId) ?? 0),
        ));

  const delayByShipmentOrderId =
    countTotalConstrainedDelayByOrder(shipmentOrderTds);

  if (opts.maxBallastDistanceNm) {
    for (let i = Math.max(0, firstShipmentTdIndex); i < timeData.length; i++) {
      const td = timeData[i];
      const ballast = isCargoEventTimeData(td) && !td.laden ? td.distance : 0;
      if (ballast > opts.maxBallastDistanceNm) {
        violations.maxBallastDistanceNm = Math.max(
          violations.maxBallastDistanceNm ?? 0,
          ballast,
        );
      }
    }
  }

  /** The max delay within the shipment */
  const shipmentDelayMaxH = Math.max(
    0,
    mappedMax(delayByShipmentOrderId.entries(), ([orderId, delay]) => {
      /** Special case: when added such that the shipment begins at an existing location, only start load delay from load start instead of its eta (if it's better than vs eta).
       * https://seaber2.myjetbrains.com/youtrack/issue/SEABER-4087 */
      if (
        shipmentBeginsInExistingPortCall &&
        isCargoEventTimeData(firstShipmentTd) &&
        orderId === firstShipmentTd.event.order.id
      ) {
        const loadTd = firstShipmentTd;
        const dischargeTd = assertedDefined(
          findLast(
            shipmentTds,
            (td) => isCargoEventTimeData(td) && td.event.order.id === orderId,
          ),
          'Order should have discharge',
        );
        return (
          Math.min(
            DELAY_CONSTRAINT_HOURS,
            Math.abs(loadTd.cancellingOrDeliveryViolationVsEta),
            Math.abs(loadTd.cancellingOrDeliveryViolationVsOperations),
            Math.abs(loadTd.cancellingOrDeliveryViolationVsEvent),
          ) +
          Math.min(
            DELAY_CONSTRAINT_HOURS,
            Math.abs(dischargeTd.cancellingOrDeliveryViolationVsEta),
          )
        );
      } else {
        return delay;
      }
    }),
  );

  /** A measure balancing both delays: existing schedule and the current shipment to assign to */
  const totalDelayH = (nonShipmentDelayMaxDeltaH ?? 0) + shipmentDelayMaxH;
  if (
    opts.maximumDelayDeltaMs &&
    hToMs(totalDelayH) > opts.maximumDelayDeltaMs
  ) {
    violations.totalDelayH = totalDelayH;
  }

  const totalMassDistance = countTotalMassDistance(
    timeData,
    deriveParams.context.distances,
  );
  const vcp = totalMassDistance / distance;

  const voyageDurationH = combinationHoursAtSea;

  const voyageDurationDeltaH = combinationHoursAtSea - originalHoursAtSea;

  const objective = opts.objective;

  /** Less is better */
  const objectiveScore =
    objective === Objective.MaximizeTCE
      ? cost - tcCost
      : objective === Objective.MinimizeCost
        ? cost
        : objective === Objective.MaximizeVCP
          ? 1 / vcp // Higher vcp is better -> inverse the direction
          : objective === Objective.ShortestDistance
            ? voyageDurationDeltaH
            : ballastDistance;

  return {
    mass,
    shipmentAmount,
    voyageDurationH,
    totalDelayH,
    objectiveScore,
    maxViolationMs,
    resultValue: {
      combination,
      timeData,
      totals,
      inputs,
      amount: shipmentAmount,
      delay: totalDelayH,
      idle: totalIdle,
      cost,
      objectiveScore,
    },
    violations,
  };
}
